/**
 * Mock API module for storefront product data + checkout flows.
 * Used while the real backend endpoints are being wired by the integration agent.
 *
 * TODO: swap each mock with real api() calls when the backend is ready:
 * - mockListProducts → api<ListOnlineCatalogResponse>('/online-catalog', ...)
 * - mockGetProductBySlug → api<OnlineCatalogEntry>(`/online-catalog/${slug}`)
 * - mockPriceCart → api<PriceCartResponse>('/cart/price', { method:'POST', ... })
 * - mockValidateDeliveryAddress → api<ValidateDeliveryAddressResponse>('/delivery/validate-address', ...)
 * - mockCreateOrder → api<Order>('/orders', { method:'POST', ... })
 */
// Placeholder image paths - will be replaced with real GCS URLs when imagery is uploaded.
// Using gradient placeholder SVGs consistent with the cream-100 → gold-100 brand palette.
const PLACEHOLDER = '/placeholder-product.svg';
export const MOCK_PRODUCTS = [
    {
        id: '11111111-0000-0000-0000-000000000001',
        productProfileId: 'ppid-0000-0000-0000-000000000001',
        publicSlug: 'tartelette-aux-figues',
        displayName: { fr: 'Tartelette aux figues', en: 'Fig tartlet' },
        displayDescription: {
            fr: "Fond de tarte sablé maison, crème d'amande dorée, figues fraîches de saison et gelée légère à la vanille. Une création fine et équilibrée.",
            en: 'Homemade shortcrust base, golden almond cream, fresh seasonal figs and a light vanilla glaze. A refined, well-balanced creation.',
        },
        onlinePrice: { amount: 9500, currency: 'MAD' }, // 95 MAD in centimes
        gallery: [PLACEHOLDER, PLACEHOLDER, PLACEHOLDER],
        displayOrder: 1,
        status: 'published',
        publishedAt: '2026-01-10T09:00:00.000Z',
        unpublishedAt: null,
        category: 'tartes',
        stockStatus: 'in_stock',
        availableUnits: null,
        isBackorder: false,
    },
    {
        id: '11111111-0000-0000-0000-000000000002',
        productProfileId: 'ppid-0000-0000-0000-000000000002',
        publicSlug: 'mille-feuille-framboise',
        displayName: { fr: 'Mille-feuille framboise', en: 'Raspberry mille-feuille' },
        displayDescription: {
            fr: 'Feuilletage caramélisé ultra-fin, crème mousseline à la framboise, framboises fraîches et coulis maison. Le classique revisité avec caractère.',
            en: 'Ultra-thin caramelised pastry layers, raspberry mousseline cream, fresh raspberries and house coulis. A classic revisited with character.',
        },
        onlinePrice: { amount: 12000, currency: 'MAD' }, // 120 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER],
        displayOrder: 2,
        status: 'published',
        publishedAt: '2026-01-12T09:00:00.000Z',
        unpublishedAt: null,
        category: 'gateaux',
        stockStatus: 'in_stock',
        availableUnits: null,
        isBackorder: false,
    },
    {
        id: '11111111-0000-0000-0000-000000000003',
        productProfileId: 'ppid-0000-0000-0000-000000000003',
        publicSlug: 'pain-au-chocolat',
        displayName: { fr: 'Pain au chocolat', en: 'Chocolate croissant' },
        displayDescription: {
            fr: 'Viennoiserie feuilletée au beurre pur AOP Charentes-Poitou, deux bâtons de chocolat noir 64% au cœur. Cuite le matin même.',
            en: 'All-butter puff pastry made with AOP Charentes-Poitou butter, two 64% dark chocolate batons at the heart. Baked the same morning.',
        },
        onlinePrice: { amount: 3500, currency: 'MAD' }, // 35 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER],
        displayOrder: 3,
        status: 'published',
        publishedAt: '2026-01-08T07:00:00.000Z',
        unpublishedAt: null,
        category: 'viennoiseries',
        stockStatus: 'low_stock',
        availableUnits: 4,
        isBackorder: false,
    },
    {
        id: '11111111-0000-0000-0000-000000000004',
        productProfileId: 'ppid-0000-0000-0000-000000000004',
        publicSlug: 'macarons-assortis',
        displayName: { fr: 'Macarons assortis', en: 'Assorted macarons' },
        displayDescription: {
            fr: 'Boîte de six macarons garnis : pistache, framboise, caramel fleur de sel, citron, rose-litchi, chocolat praliné. Coques lisses, ganaches généreuses.',
            en: 'Box of six filled macarons: pistachio, raspberry, salted caramel, lemon, rose-lychee, praline chocolate. Smooth shells, generous ganaches.',
        },
        onlinePrice: { amount: 8000, currency: 'MAD' }, // 80 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER, PLACEHOLDER],
        displayOrder: 4,
        status: 'published',
        publishedAt: '2026-01-15T09:00:00.000Z',
        unpublishedAt: null,
        category: 'biscuits',
        stockStatus: 'in_stock',
        availableUnits: null,
        isBackorder: false,
    },
    {
        id: '11111111-0000-0000-0000-000000000005',
        productProfileId: 'ppid-0000-0000-0000-000000000005',
        publicSlug: 'gateau-aux-amandes',
        displayName: { fr: 'Gâteau aux amandes', en: 'Almond cake' },
        displayDescription: {
            fr: "Moelleux fondant aux amandes de Meknès, légèrement parfumé à la fleur d'oranger et au citron confit. Sans gluten, sans lactose.",
            en: 'Soft and moist almond cake made with Meknes almonds, lightly scented with orange blossom and preserved lemon. Gluten-free, dairy-free.',
        },
        onlinePrice: { amount: 11000, currency: 'MAD' }, // 110 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER],
        displayOrder: 5,
        status: 'published',
        publishedAt: '2026-01-20T09:00:00.000Z',
        unpublishedAt: null,
        category: 'sans-gluten',
        stockStatus: 'in_stock',
        availableUnits: null,
        isBackorder: false,
    },
    {
        id: '11111111-0000-0000-0000-000000000006',
        productProfileId: 'ppid-0000-0000-0000-000000000006',
        publicSlug: 'tarte-citron-meringuee',
        displayName: { fr: 'Tarte citron meringuée', en: 'Lemon meringue tart' },
        displayDescription: {
            fr: 'Fond sablé breton croustillant, curd de citron de Menton vif et soyeux, meringue italienne torchée à la flamme. Un équilibre acidulé-sucré parfait.',
            en: 'Crisp Breton shortbread base, vibrant and silky Menton lemon curd, flame-torched Italian meringue. Perfect sweet-tart balance.',
        },
        onlinePrice: { amount: 10500, currency: 'MAD' }, // 105 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER, PLACEHOLDER],
        displayOrder: 6,
        status: 'published',
        publishedAt: '2026-01-18T09:00:00.000Z',
        unpublishedAt: null,
        category: 'tartes',
        stockStatus: 'in_stock',
        availableUnits: null,
        isBackorder: false,
    },
    {
        id: '11111111-0000-0000-0000-000000000007',
        productProfileId: 'ppid-0000-0000-0000-000000000007',
        publicSlug: 'croissant-au-beurre',
        displayName: { fr: 'Croissant au beurre', en: 'Butter croissant' },
        displayDescription: {
            fr: 'Feuilletage au beurre de qualité supérieure, 27 couches caramélisées, alvéoles aérées. Le croissant dans toute sa noblesse.',
            en: 'Premium butter puff pastry, 27 caramelised layers, open airy crumb. Croissant in its finest form.',
        },
        onlinePrice: { amount: 3000, currency: 'MAD' }, // 30 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER],
        displayOrder: 7,
        status: 'published',
        publishedAt: '2026-01-05T07:00:00.000Z',
        unpublishedAt: null,
        category: 'viennoiseries',
        stockStatus: 'low_stock',
        availableUnits: 6,
        isBackorder: false,
    },
    {
        id: '11111111-0000-0000-0000-000000000008',
        productProfileId: 'ppid-0000-0000-0000-000000000008',
        publicSlug: 'entremet-chocolat-caramel',
        displayName: { fr: 'Entremets chocolat-caramel', en: 'Chocolate-caramel entremet' },
        displayDescription: {
            fr: 'Mousse au chocolat Valrhona Manjari 64%, insert caramel tendre au beurre salé, biscuit Joconde, glaçage miroir satiné.',
            en: 'Valrhona Manjari 64% chocolate mousse, salted butter caramel insert, Joconde sponge, satin mirror glaze.',
        },
        onlinePrice: { amount: 18000, currency: 'MAD' }, // 180 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER, PLACEHOLDER],
        displayOrder: 8,
        status: 'published',
        publishedAt: '2026-02-01T09:00:00.000Z',
        unpublishedAt: null,
        category: 'gateaux',
        stockStatus: 'out_of_stock',
        availableUnits: 0,
        isBackorder: true,
    },
    {
        id: '11111111-0000-0000-0000-000000000009',
        productProfileId: 'ppid-0000-0000-0000-000000000009',
        publicSlug: 'financiers-pistache',
        displayName: { fr: 'Financiers pistache', en: 'Pistachio financiers' },
        displayDescription: {
            fr: "Boîte de cinq financiers à la pâte de pistache pure de Sicile, beurre noisette, blancs d'œufs montés. Croustillants dehors, fondants dedans.",
            en: 'Box of five financiers made with pure Sicilian pistachio paste, brown butter, whipped egg whites. Crisp outside, melt-in-the-mouth inside.',
        },
        onlinePrice: { amount: 7500, currency: 'MAD' }, // 75 MAD
        gallery: [PLACEHOLDER, PLACEHOLDER],
        displayOrder: 9,
        status: 'published',
        publishedAt: '2026-02-10T09:00:00.000Z',
        unpublishedAt: null,
        category: 'biscuits',
        stockStatus: 'in_stock',
        availableUnits: null,
        isBackorder: false,
    },
];
/** Simulate API latency (300ms) */
const delay = (ms = 300) => new Promise((r) => setTimeout(r, ms));
export async function mockListProducts(q) {
    await delay();
    const limit = q.limit ?? 20;
    const startIndex = q.cursor ? parseInt(q.cursor, 10) : 0;
    const slice = MOCK_PRODUCTS.slice(startIndex, startIndex + limit);
    const nextStart = startIndex + limit;
    const nextCursor = nextStart < MOCK_PRODUCTS.length ? String(nextStart) : null;
    return { entries: slice, nextCursor };
}
export async function mockGetProductBySlug(slug) {
    await delay(200);
    return MOCK_PRODUCTS.find((p) => p.publicSlug === slug) ?? null;
}
// ---------------------------------------------------------------------------
// Cart pricing mock
// ---------------------------------------------------------------------------
export async function mockPriceCart(req) {
    await delay(180);
    const deliveryFeeAmount = 0; // Delivery disabled; always 0 MAD
    const lines = req.cart.items.map((item) => {
        const product = MOCK_PRODUCTS.find((p) => p.publicSlug === item.publicSlug);
        const unitPrice = product?.onlinePrice.amount ?? 8000;
        const lineTotal = unitPrice * item.quantity;
        const availableUnits = product?.availableUnits ?? null;
        const isOutOfStock = product?.stockStatus === 'out_of_stock';
        const isOverStock = product?.stockStatus === 'low_stock' &&
            availableUnits !== null &&
            item.quantity > availableUnits;
        return {
            productProfileId: item.productProfileId,
            publicSlug: item.publicSlug,
            // Slug is NEVER user-visible. Prefer cart-store snapshot when present
            // (frontend CartItem extends with displayName), else em-dash.
            displayName: product?.displayName
                ?? item.displayName
                ?? { fr: '-', en: '-' },
            quantity: item.quantity,
            unitPrice: { amount: unitPrice, currency: 'MAD' },
            lineTotal: { amount: lineTotal, currency: 'MAD' },
            // Always expose the live cap so cart UIs can disable + at the limit,
            // not only when over-cap. null means unconstrained (in_stock).
            availableUnits: availableUnits,
            availabilityError: isOutOfStock || isOverStock
                ? { availableUnits: availableUnits ?? 0 }
                : null,
        };
    });
    const subtotalAmount = lines.reduce((sum, l) => sum + l.lineTotal.amount, 0);
    return {
        lines,
        subtotal: { amount: subtotalAmount, currency: 'MAD' },
        deliveryFee: { amount: deliveryFeeAmount, currency: 'MAD' },
        total: { amount: subtotalAmount + deliveryFeeAmount, currency: 'MAD' },
    };
}
// ---------------------------------------------------------------------------
// Delivery address validation mock
// Tangier bounding box: lat 35.70–35.82, lng -5.95 – -5.70
// ---------------------------------------------------------------------------
export async function mockValidateDeliveryAddress(req) {
    await delay(250);
    const inside = req.lat >= 35.7 && req.lat <= 35.82 && req.lng >= -5.95 && req.lng <= -5.7;
    if (!inside) {
        return { isValid: false, reason: 'outside_tangier', fee: null };
    }
    return { isValid: true, reason: 'ok', fee: { amount: 3000, currency: 'MAD' } };
}
// ---------------------------------------------------------------------------
// Create order mock
// ---------------------------------------------------------------------------
export async function mockCreateOrder(input) {
    await delay(400);
    const now = new Date().toISOString();
    const lines = input.cart.items.map((item) => {
        const product = MOCK_PRODUCTS.find((p) => p.publicSlug === item.publicSlug);
        const unitPrice = product?.onlinePrice.amount ?? 8000;
        const lineTotal = unitPrice * item.quantity;
        return {
            productProfileId: item.productProfileId,
            slugSnapshot: item.publicSlug,
            displayNameSnapshot: product?.displayName
                ?? item.displayName
                ?? { fr: '-', en: '-' },
            unitPrice: { amount: unitPrice, currency: 'MAD' },
            quantity: item.quantity,
            lineTotal: { amount: lineTotal, currency: 'MAD' },
        };
    });
    const subtotalAmount = lines.reduce((sum, l) => sum + l.lineTotal.amount, 0);
    const deliveryFeeAmount = 0; // Delivery disabled; always 0 MAD
    return {
        id: crypto.randomUUID(),
        status: 'awaiting_payment',
        lines,
        subtotal: { amount: subtotalAmount, currency: 'MAD' },
        deliveryFee: { amount: deliveryFeeAmount, currency: 'MAD' },
        total: { amount: subtotalAmount + deliveryFeeAmount, currency: 'MAD' },
        fulfillment: input.fulfillment,
        customer: input.customer,
        createdAt: now,
        paidAt: null,
        fulfilledAt: null,
        cancelledAt: null,
        posDelivery: { status: 'pending', sentAt: null, ackedAt: null, deviceCode: null },
    };
}
